import { useCallback, useEffect, useRef, useState } from 'react';
import { CANVAS_PAD, usePagePreviewScale } from '../shared/usePagePreviewScale';
import FuelCardFormPreview, {
    FUEL_CARD_PAGE_HEIGHT,
    FUEL_CARD_PAGE_WIDTH,
} from './FuelCardFormPreview';

/**
 * The letter inside the preview rail.
 *
 * The paper is scaled to fit the card rather than scrolled around — a letter
 * you have to pan to read is no use as a preview — so the whole page is on
 * screen at once. Zoomed in past the card, it is dragged around instead.
 *
 * This one is a single sheet whose height is a *minimum*: a long family list or
 * a long address pushes it past A4. The rendered height is measured rather than
 * assumed, so the scroll box always matches what the transform paints.
 */
export default function FuelCardPreviewFrame({ form, previewRef, zoom = 1 }) {
    const paperRef = useRef(null);
    const [documentHeight, setDocumentHeight] = useState(FUEL_CARD_PAGE_HEIGHT);
    const [scrollRef, scale] = usePagePreviewScale(FUEL_CARD_PAGE_WIDTH, documentHeight, zoom);

    const assignPreviewRef = useCallback(
        (node) => {
            paperRef.current = node;
            if (typeof previewRef === 'function') {
                previewRef(node);
            } else if (previewRef) {
                previewRef.current = node;
            }
        },
        [previewRef],
    );

    useEffect(() => {
        const el = paperRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;

        const measure = () => setDocumentHeight(
            Math.max(FUEL_CARD_PAGE_HEIGHT, el.offsetHeight),
        );

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [form]);

    return (
        <div
            ref={scrollRef}
            className="w-full min-h-0 flex-1 select-none overflow-auto rounded-lg bg-[#3d3d3d]"
            style={{ padding: CANVAS_PAD }}
        >
            {/* The scaled paper is laid out at full size, so the scroll box is
                given the height the transform actually paints. */}
            <div
                style={{
                    height: documentHeight * scale,
                    width: FUEL_CARD_PAGE_WIDTH * scale,
                    margin: '0 auto',
                }}
            >
                <div
                    className="shadow-[0_8px_32px_rgba(0,0,0,0.35)]"
                    style={{
                        width: FUEL_CARD_PAGE_WIDTH,
                        transform: `scale(${scale})`,
                        transformOrigin: 'top left',
                    }}
                >
                    <FuelCardFormPreview ref={assignPreviewRef} form={form} />
                </div>
            </div>
        </div>
    );
}
